import ResponseError from '@intigral/domain/lib/core/entities/errors/ResponseError';
import { FlexWrapper, LinkWrapper, SiteLogo, Skeleton, Text, toast } from '@intigral/shared';
import { FlexChild } from '@intigral/shared/components/FlexWrapper';
import styles from '@intigral/shared/styles/pages/profiles.module.sass';
import { get, set } from '@intigral/utils/lib/browser-storage/localStorage';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { BRAZE } from '../../config/analytics-events';
import { screenDetailTypes, STORE_NAME } from '../../config/constants';
import { EVENT, KEYS } from '../../contexts/MixpanelConstants';
import { MixpanelContext } from '../../contexts/MixpanelContext';
import {
    getMixpanelProfileList,
    getMixpanelProfileName,
    getMixpanelProfilesCount,
} from '../../contexts/MixpanelHelper';
import h from '../../helpers/common';
import { getSelectedProfileAvatars } from '../../helpers/profile';
import url from '../../helpers/url';
import user from '../../helpers/user';
import useBraze from '../../hooks/useBraze';
import useProfileAvatars from '../../hooks/useProfileAvatars';
import { useProfileSwitching } from '../../hooks/useProfileSwitching';
import { IStore } from '../../interfaces/StoreTypes';
import {
    ClearDispatchedActions,
    LoadProfilesList,
    selectProfileDone,
    switchProfileMutationFn,
} from '../../store/reducers/profileSlice';
import { __ } from '../../translations';
import { getUserInfo } from '../../utils/user';
import { useProfileList } from '../../utils/useUserProfile';
import { useConfigurationSections } from '../../hooks/useConfiguration';
import PasswordPopup from '../elements/PasswordPopup';
import { useMediaQuery } from '@intigral/hooks';
import { type IProfileData } from '@intigral/types';

// Configuration is now fetched from the redux store instead of localStorage
// to avoid initialization issues when the user first logs in.

// Survives AppWrapper router remount after selectProfileDone so soft navigate can run
// once route guards already see the selected profile.
let pendingProfileOpenUrl: string | null = null;

export const SelectProfile = ({ data, ...props }) => {
    const showKidsOnly = url.getQuery('kids') ?? false;
    const [showProfiles, setShowProfiles] = useState(false);
    const { data: profileList } = useProfileList(getUserInfo());
    const { dispatchedActions } = useSelector((state: IStore) => state.profile);
    const selectedProfile = useSelector((state: IStore) => state.profile?.selectedProfile) || ({} as IProfileData);
    // Using React Query instead of Redux for configuration
    const { profile: profileConfig } = useConfigurationSections();
    const profileColors = profileConfig?.colors || [];
    const {
        track,
        setUserProperties,
        setSuperProperties,
    }: { track: any; setUserProperties: any; setSuperProperties: any } = useContext(MixpanelContext);
    const { logBrazeCustomEvent } = useBraze();
    const dispatch = useDispatch();
    const loadProfilesList = useCallback(() => dispatch(LoadProfilesList(false)), [dispatch]);
    // const loadProfileData = useCallback((profileData) => dispatch(LoadProfile(profileData)), [dispatch]);
    const clearDispatchedActions = useCallback(() => dispatch(ClearDispatchedActions()), [dispatch]);

    const [showDoneBtn, setShowDoneBtn] = useState(false);
    const [showCloseBtn, setShowCloseBtn] = useState(false);
    const [selectedProfileGuid, setSelectedProfileGuid] = useState('');
    const [eligibleToAdd, setEligibleToAdd] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [profileSwitched, setProfileSwitched] = useState(false);

    // password popup states
    const [showPasswordPopup, setShowPasswordPopup] = useState(false);
    const [passwordVerified, setPasswordVerified] = useState(false);
    const [clickedProfile, setClickedProfile] = useState(null);
    const { data: profileAvatars } = useProfileAvatars();

    const prevSelectedProfile = get(STORE_NAME.SELECTED_PROFILE) || {};
    const location = useLocation();
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const switchProfileMutation = useMutation({
        mutationFn: switchProfileMutationFn,
        onSuccess: ({ result, profile }) => {
            const { identityToken, showWelcomeScreen, liveLikeAccessToken = null, liveLikeProfileId = null } = result;
            const currentStoredUserData = getUserInfo();
            set(STORE_NAME.USER, { ...currentStoredUserData, identityToken });

            // For kids profiles, ensure showWelcomeScreen is true regardless of server response
            // This ensures the gamification welcome modal appears for all kids profiles
            const shouldShowWelcomeScreen = profile.kidsMode ? true : showWelcomeScreen;

            const updatedProfile = {
                ...profile,
                date: Date.now(),
                showWelcomeScreen: shouldShowWelcomeScreen,
                liveLikeAccessToken,
                liveLikeProfileId,
            };
            set(STORE_NAME.SELECTED_PROFILE, updatedProfile);
            const { urlToOpenTheProfile } = useProfileSwitching(profile as any);
            pendingProfileOpenUrl = urlToOpenTheProfile;
            dispatch(selectProfileDone(updatedProfile));
        },
        onError: (error: ResponseError | { code?: string } | undefined) => {
            // switchProfileMutationFn throws error.data, so the payload may be either
            // ResponseError or the bare error data object.
            const errCode = (error as ResponseError)?.data?.code ?? (error as { code?: string })?.code;
            // The errorCode '9031' is for "Incorrect password", but for B2B the message should be "Incorrect account PIN"
            const localizedKey = errCode ? (errCode === '9031' ? B2B_localization : '') + `err_msg_${errCode}` : '';
            let localizedError = errCode ? __(localizedKey) : __('general_error_message');
            if (!errCode || localizedKey === localizedError)
                // If no localization found, fallback to general error message
                localizedError = __('general_error_message');
            toast(localizedError, 'error');
        },
    });

    // Soft-navigate after selectedProfile is committed so main-app route guards don't bounce back.
    useEffect(() => {
        if (!pendingProfileOpenUrl || !selectedProfile?.profileGuid) {
            return;
        }
        const url = pendingProfileOpenUrl;
        pendingProfileOpenUrl = null;
        navigate(url, { replace: true });
        queryClient.invalidateQueries({ queryKey: ['profiles'] });
        queryClient.invalidateQueries({ queryKey: ['cms-load'] });
        setProfileSwitched(true);
    }, [selectedProfile?.profileGuid, selectedProfile?.date, navigate, queryClient]);

    const validatePasswordAndSwitchProfile = ({ profileData, password }) => {
        return switchProfileMutation.mutateAsync({
            ...profileData,
            passwordProtected: password,
            profilePassword: password,
        });
    };

    // useEffect(() => {
    //     dispatch(setProfileAvatars(profileAvatars));
    // }, [dispatch, profileAvatars]);

    useEffect(() => {
        // load profile and close popup after password Verified
        if (passwordVerified) {
            setPasswordVerified((s) => {
                setSuperProperties({
                    [KEYS.SUPER.ACTIVE_PROFILE]: clickedProfile.profileName,
                });
                track(EVENT.PROFILE.PROFILES_SELECT, getMixpanelProfileName(clickedProfile.profileName));
                setClickedProfile(null);
                setShowPasswordPopup(false);
                setProfileSwitched(true);
                return false;
            });
        }
    }, [passwordVerified]);

    // load profile or show password popup
    const loadProfileFunctions = async () => {
        setSuperProperties({
            [KEYS.SUPER.ACTIVE_PROFILE]: clickedProfile.profileName,
        });
        track(EVENT.PROFILE.PROFILES_SELECT, getMixpanelProfileName(clickedProfile.profileName));
        switchProfileMutation.mutate(clickedProfile);
    };

    useEffect(() => {
        if (clickedProfile) {
            // const selectedProfile = get(STORE_NAME.SELECTED_PROFILE) ?? selectedProfile;
            if (
                !editMode ||
                (editMode && selectedProfile.kidsMode && clickedProfile.profileGuid !== selectedProfile.profileGuid)
            ) {
                if (clickedProfile.passwordProtected && !passwordVerified) {
                    setShowPasswordPopup(true);
                } else {
                    loadProfileFunctions();
                }
            } else {
                track(EVENT.PROFILE.PROFILES_EDIT, getMixpanelProfileName(clickedProfile.profileName));
            }
        }
    }, [clickedProfile]);

    useEffect(() => {
        // enable edit mode if user navigate from menu
        if (location && typeof location.state !== 'undefined' && location.state?.editMode) {
            setEditMode(true);
            setShowDoneBtn(true);
        }
    }, [location.pathname, location.search]);

    const handleEditProfiles = () => {
        setEditMode(true);
        setShowDoneBtn(true);
    };

    const handleEditProfilesDone = () => {
        setEditMode(false);
        setShowDoneBtn(false);
    };

    const closePasswordPopupHandler = () => {
        setShowPasswordPopup(false);
        setClickedProfile(null);
    };

    const selectProfileHandler = (e, clickedProfile) => {
        e.preventDefault();
        if (editMode) {
            if (selectedProfile.kidsMode && clickedProfile.profileGuid === selectedProfile.profileGuid) {
                navigate(url.route(`edit-profile?guid=${btoa(clickedProfile.profileGuid)}`));
            }

            if (!selectedProfile.kidsMode) {
                navigate(url.route(`edit-profile?guid=${btoa(clickedProfile.profileGuid)}`));
            }
        } else {
            setClickedProfile(clickedProfile);
            logBrazeCustomEvent(BRAZE.EVENTS.APP_OPEN);
        }
        return false;
    };

    useEffect(() => {
        if (profileList && !editMode) {
            setUserProperties({
                [KEYS.PEOPLE.USER_PROFILES]: getMixpanelProfileList(profileList?.profiles),
            });
            track(EVENT.PROFILE.PROFILES_VIEW, getMixpanelProfilesCount(Object.values(profileList?.profiles).length));
        }
    }, [editMode]);

    useEffect(() => {
        if (profileList !== null) {
            if (selectedProfile && typeof selectedProfile.profileGuid !== 'undefined') {
                setSelectedProfileGuid(selectedProfile.profileGuid);
                setShowCloseBtn(true);
            }
        } else {
            loadProfilesList();
        }
        setEligibleToAdd(Object.values(profileList?.profiles ?? {})?.length < profileList?.profilesLimit);

        if (profileList?.profiles.length > 0) {
            setShowProfiles(true);
        }
    }, [selectedProfile, profileList, profileSwitched]);

    // clear store to hide toast msg
    useEffect(() => {
        if (
            dispatchedActions?.action !== 'hasBeenDeleted' &&
            typeof dispatchedActions.message === 'object' &&
            dispatchedActions?.message !== null
        ) {
            toast(
                __(`manage_profile_${dispatchedActions.action}_successfully`).replace(
                    '{profile Name}',
                    dispatchedActions.message?.profileName || ''
                ),
                'success'
            );

            setTimeout(() => {
                clearDispatchedActions();
            }, 2500);
        }
    }, [dispatchedActions]);

    // If the flag (Account retrieval feature) is enabled:
    //      Check if the profile is locked with parental lock
    // otherwise:
    //      Assume the profile is not locked with parental lock
    // const profileIsLocked = h.isAccountRetrievalFeatureEnabled() ? selectedProfile.parentalLockOn : false;

    // Because some localization on B2B is different, for example "Password" should be "Account PIN" on B2B
    // If account type is B2B, Then this will be used to add "b2b_" just before the localization key
    const B2B_localization = user.getUserSegment() === 'B2B' ? 'b2b_' : '';

    const isControlKeysPressed = (e) => {
        return e.metaKey || e.shiftKey || e.ctrlKey || e.altKey;
    };

    const handleSwitchLanguage = (e) => {
        if (isControlKeysPressed(e)) {
            return;
        }
        h.switchLang(h.getLang() === 'en' ? 'ar' : 'en');

        // Triggering event to detect language switch
        const event = new Event('languageSwitched');
        document.dispatchEvent(event);
    };

    function routeWithLanguage(currentUrl, lang) {
        // Validate the input language
        if (!['en', 'ar'].includes(lang)) {
            throw new Error('Invalid language. Only "en" and "ar" are supported.');
        }

        // Parse the current URL
        const url = new URL(currentUrl);

        // Extract the path segments
        const pathParts = url.pathname.split('/').filter(Boolean);

        // Check if a language prefix already exists (e.g., "en" or "ar")
        if (pathParts[0] === 'en' || pathParts[0] === 'ar') {
            // Replace the first segment with the new language
            pathParts[0] = lang;
        } else {
            // Prepend the new language to the path
            pathParts.unshift(lang);
        }

        // Construct the new path
        url.pathname = `/${pathParts.join('/')}`;

        // Return the updated URL as a string
        return url.toString();
    }

    const currentUrl = window.location.href;
    const oppositeLang = h.getLang() === 'en' ? 'ar' : 'en';

    const isMobile = useMediaQuery('(max-width: 924px)');

    return (
        <div id="wrapper" style={{ height: '100vh' }}>
            <FlexWrapper gap="4x" padding={{ default: '2x', md: '5x' }} justifyContent="between" alignItems="center">
                <FlexChild alignItems="center">
                    <SiteLogo
                        {...{
                            homeUrl: !selectedProfile?.kidsMode ? '/' : 'kids-home',
                            title: 'STC logo',
                            logoSrc: 'images/big-logo.svg',
                            LinkComponent: Link,
                        }}
                        className={styles.logo}
                    />
                </FlexChild>
                <FlexWrapper gap="2x">
                    <FlexChild gap="2x">
                        {!showKidsOnly &&
                            (showDoneBtn ? (
                                <LinkWrapper
                                    link={{
                                        linkComponent: Link,
                                        href: '#',
                                        text: __('done'),
                                        transformLinkProps: () => ({
                                            onClick: (e) => {
                                                e.preventDefault();
                                                handleEditProfilesDone();
                                            },
                                        }),
                                    }}
                                    data-test="done-profile"
                                    className="primary-brand"
                                />
                            ) : (
                                <LinkWrapper
                                    link={{
                                        linkComponent: Link,
                                        href: '#',
                                        text: __('manage_profiles'),
                                        transformLinkProps: () => ({
                                            onClick: (e) => {
                                                e.preventDefault();
                                                handleEditProfiles();
                                            },
                                        }),
                                    }}
                                    data-test="edit-profile"
                                    className="primary-brand"
                                />
                            ))}
                    </FlexChild>
                    <FlexChild>
                        {h.getLang() === 'en' ? (
                            <LinkWrapper
                                className="primary-brand"
                                link={{
                                    href: '/',
                                    linkComponent: Link,
                                    text: __('settings_lang_Arabic'),
                                    transformLinkProps: () => ({
                                        to: routeWithLanguage(currentUrl, oppositeLang),
                                        onClick: handleSwitchLanguage,
                                    }),
                                }}
                            />
                        ) : (
                            <LinkWrapper
                                className="primary-brand"
                                link={{
                                    href: 'en',
                                    linkComponent: Link,
                                    text: __('settings_lang_English'),
                                    transformLinkProps: () => ({
                                        to: routeWithLanguage(currentUrl, oppositeLang),
                                        onClick: handleSwitchLanguage,
                                    }),
                                }}
                            />
                        )}
                    </FlexChild>
                </FlexWrapper>
            </FlexWrapper>
            <div
                className="container full-width"
                {...(!isMobile && { style: { paddingBottom: 'calc(var(--step) * 10)' } })}
            >
                {profileList && profileList?.profiles && showProfiles && profileList?.profiles.length ? (
                    <>
                        <>
                            {editMode ? (
                                <Text as="h2" variant="h2">
                                    {__('manage_profile_edit_your_profiles')}
                                </Text>
                            ) : (
                                <Text as="h2" variant="h2">
                                    {__('manage_profile_who_is_watching')}
                                </Text>
                            )}

                            <div className={styles['kids-profiles']}>
                                {profileList?.profiles.map((profile, index) => {
                                    // Current profile is kid profile OR locked with parental lock?
                                    return (
                                        (showKidsOnly
                                            ? // Show the kids profiles.
                                              profile.kidsMode
                                            : // Show all profiles if current profile is not a kid profile, otherwise show only kids profiles.
                                              !selectedProfile.kidsMode ||
                                              (selectedProfile.kidsMode && profile.kidsMode)) && (
                                            <a
                                                href="#"
                                                className={`${styles['kids-profile-item']} ${styles.locked} ${
                                                    (!!editMode && !selectedProfile.kidsMode) ||
                                                    (!!editMode &&
                                                        selectedProfile.kidsMode &&
                                                        profile.profileGuid === selectedProfile.profileGuid)
                                                        ? styles.editing
                                                        : ''
                                                }`}
                                                style={{
                                                    position: 'relative',
                                                    backgroundImage: profile.passwordProtected
                                                        ? 'url(../images/lock.svg)'
                                                        : '',
                                                }}
                                                key={index}
                                                id={`selectProfile_profile${index}`}
                                                onClick={(e) => selectProfileHandler(e, profile)}
                                            >
                                                {/* Show regular icon */}
                                                {!profile.kidsMode && (
                                                    <div
                                                        className={styles.ico}
                                                        style={{
                                                            backgroundColor: profileColors && profile?.avatar,
                                                            backgroundImage: `url(${getSelectedProfileAvatars(
                                                                profile,
                                                                profileAvatars
                                                            )})`,
                                                        }}
                                                    >
                                                        {((!!editMode && !selectedProfile.kidsMode) ||
                                                            (!!editMode &&
                                                                selectedProfile.kidsMode &&
                                                                profile.profileGuid ===
                                                                    selectedProfile.profileGuid)) && (
                                                            <span className={styles['edit-ico']}>
                                                                <img src="../images/edit-pen.svg" alt="" />
                                                            </span>
                                                        )}
                                                    </div>
                                                )}

                                                {/* Show Kids Icon */}
                                                {profile.kidsMode && (
                                                    <div
                                                        className={styles.ico}
                                                        style={
                                                            {
                                                                //backgroundColor: profileColors && profile?.avatar,
                                                                //backgroundImage: `url(${profile.avatar})`,
                                                            }
                                                        }
                                                    >
                                                        {/* <img src={user.getProfileAvatar('kid')} alt="" /> */}
                                                        <img
                                                            src={getSelectedProfileAvatars(profile, profileAvatars)}
                                                            alt=""
                                                        />

                                                        {((!!editMode && !selectedProfile.kidsMode) ||
                                                            (!!editMode &&
                                                                selectedProfile.kidsMode &&
                                                                profile.profileGuid ===
                                                                    selectedProfile.profileGuid)) && (
                                                            <span className={styles['edit-ico']}>
                                                                <img src="../images/edit-pen.svg" alt="" />
                                                            </span>
                                                        )}
                                                    </div>
                                                )}

                                                <strong className={styles.title}>{profile.profileName} </strong>
                                                <span className={styles.permission}>
                                                    {__(
                                                        'parentalControl_' +
                                                            (profile.newParentalControl || profile.parentalControl)
                                                    )}
                                                </span>
                                            </a>
                                        )
                                    );
                                })}

                                {!!eligibleToAdd && !selectedProfile.kidsMode && !showKidsOnly && (
                                    <Link
                                        to={url.route('add-profile')}
                                        state={{
                                            fromRoute: url.getDetailsPage(screenDetailTypes.PROFILE_SELECT),
                                        }}
                                        className={`${styles['kids-profile-item']} ${styles['add-profile']}`}
                                        data-test-id={`btnAddProfile`}
                                    >
                                        <div className={styles.ico}>
                                            <img src="../images/plus-profile.svg" alt="" />
                                        </div>
                                        <strong className={styles.title}>{__('add_profiles')}</strong>
                                        <span className="help-text">{__('add_profiles_hint')}</span>
                                    </Link>
                                )}
                            </div>
                        </>
                    </>
                ) : (
                    <div className={styles['kids-profiles']}>
                        {[...Array(3).keys()].map((_, idx) => {
                            return (
                                <div
                                    className={styles['kids-profile-item']}
                                    style={{ backgroundColor: 'transparent' }}
                                    key={`profile-skeleton${idx}`}
                                >
                                    <Skeleton absolute />
                                </div>
                            );
                        })}
                    </div>
                )}
                <PasswordPopup
                    description={__(B2B_localization + 'password_popup_description_selectProfile')}
                    showPopup={showPasswordPopup}
                    profile={clickedProfile ? clickedProfile : {}}
                    onSuccess={() => setPasswordVerified(true)}
                    onClose={() => closePasswordPopupHandler()}
                    verifyPasswordFunction={validatePasswordAndSwitchProfile}
                    shouldDispatch={false}
                />
            </div>
        </div>
    );
};
